import React, { useMemo, useState } from 'react';
import { useToast } from '@os/hooks/use-toast';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Textarea } from '@os/components/ui/textarea';
import { Input } from '@os/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@os/components/ui/tabs';
import { ScrollArea } from '@os/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Sparkles, Wand2, Wrench, FileCode, FileCog, FileCheck, Copy, Check, RefreshCw } from 'lucide-react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import type { AIModel } from '@/types';
import { sendMessageToModel } from '@/services/ModelService';

type AIBuildPanelProps = {
  editorRef: any;
  activeFileName?: string;
  language?: string;
};

const presets = {
  explain: 'Explain the following code step by step, highlighting key concepts, potential pitfalls, and runtime behavior.',
  refactor: 'Refactor the following code to be cleaner, more modular, and easier to maintain. Preserve functionality. Only output the updated code.',
  fix: 'Identify and fix bugs or issues in the following code. Explain your changes briefly, then provide the corrected code.',
  tests: 'Write comprehensive unit tests for the following code. Use sensible test organization and coverage. Only output the test code.',
  docs: 'Add clear, developer-friendly documentation and docstrings to the following code. Only output the updated code with docs.',
};

const panelHelp = `AI Builder can act on the current selection or the entire file:
- Explain: Understand code behavior
- Refactor: Improve structure & readability
- Fix: Correct issues
- Tests: Generate unit tests
- Docs: Add docstrings/comments
Use "Apply" to replace the selection (or insert at cursor) with generated code.`;

export default function AIBuildPanel({ editorRef, activeFileName, language }: AIBuildPanelProps) {
  const { toast } = useToast();
  const { models: supabaseModels } = useSupabaseStore();

  const activeModels = useMemo(() => (supabaseModels || []).filter(m => m.is_active !== false), [supabaseModels]);
  const [modelId, setModelId] = useState<string>(activeModels[0]?.id || '');
  const selectedModel = useMemo<AIModel | null>(() => activeModels.find(m => m.id === modelId) || null, [activeModels, modelId]);

  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [lastAction, setLastAction] = useState<'chat'|'explain'|'refactor'|'fix'|'tests'|'docs'|'none'>('none');

  const getSelectionText = (): string => {
    if (!editorRef?.current) return '';
    const editor = editorRef.current;
    const selection = editor.getSelection();
    const model = editor.getModel();
    if (!selection || !model) return '';
    return model.getValueInRange(selection);
  };

  const getFullText = (): string => {
    const model = editorRef?.current?.getModel?.();
    return model?.getValue?.() || '';
  };

  const applyOutput = () => {
    if (!editorRef?.current) return;
    if (!output.trim()) {
      toast({ title: 'No output to apply', description: 'Generate some code first.', variant: 'destructive' });
      return;
    }
    const editor = editorRef.current;
    const selection = editor.getSelection();
    const model = editor.getModel();

    // If there's a selection, replace it; otherwise insert at cursor
    const textToInsert = output;
    if (selection && model) {
      const hasSelection = !selection.isEmpty();
      if (hasSelection) {
        model.pushEditOperations([], [{ range: selection, text: textToInsert }], () => null);
      } else {
        const position = editor.getPosition();
        model.pushEditOperations([], [{ range: new (window as any).monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column), text: textToInsert }], () => null);
      }
      toast({ title: 'Applied', description: 'Inserted generated code into the editor.' });
    } else {
      toast({ title: 'Apply failed', description: 'No active editor or model.', variant: 'destructive' });
    }
  };

  const copyOutput = async () => {
    try {
      await navigator.clipboard.writeText(output || '');
      toast({ title: 'Copied', description: 'AI output copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy output.', variant: 'destructive' });
    }
  };

  const runAI = async (action: typeof lastAction, customPrompt?: string) => {
    if (!selectedModel) {
      toast({ title: 'No model available', description: 'Please configure models in Settings.', variant: 'destructive' });
      return;
    }

    setIsRunning(true);
    setLastAction(action);

    const selection = getSelectionText();
    const full = getFullText();
    const codeContext = selection.trim() ? selection : full;
    const contextLabel = selection.trim() ? 'selection' : 'file';
    const header = `File: ${activeFileName || '(untitled)'} • Language: ${language || 'unknown'} • Context: ${contextLabel}`;
    const userPrompt = customPrompt?.trim() || prompt.trim();

    let system = '';
    switch (action) {
      case 'explain': system = presets.explain; break;
      case 'refactor': system = presets.refactor; break;
      case 'fix': system = presets.fix; break;
      case 'tests': system = presets.tests; break;
      case 'docs': system = presets.docs; break;
      default: system = 'You are a helpful AI code assistant. Provide concise, high-quality outputs.';
    }

    const messages: Array<{ role: string; content: any }> = [
      { role: 'system', content: `${system}\n${header}` },
      { role: 'user', content: userPrompt ? `${userPrompt}\n\nCODE:\n${codeContext}` : `CODE:\n${codeContext}` },
    ];

    try {
      const response = await sendMessageToModel(messages as any, selectedModel, activeModels);
      const text = response?.content || '';
      setOutput(text);
    } catch (err: any) {
      toast({ title: 'AI error', description: err?.message || 'Failed to get AI response.', variant: 'destructive' });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="py-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="w-4 h-4 text-primary" />
          AI Builder
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3 pt-0">
        {/* Model selection */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Model</label>
          <Select value={modelId} onValueChange={(v) => setModelId(v)}>
            <SelectTrigger className="h-8 w-full">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {activeModels.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} <span className="text-muted-foreground text-xs">({m.provider})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="actions" className="flex-1 flex flex-col">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="actions">Actions</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
          </TabsList>

          {/* Actions tab */}
          <TabsContent value="actions" className="flex-1 m-0 pt-2">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => runAI('explain')} disabled={isRunning} className="justify-start">
                  <Wand2 className="w-4 h-4" /> Explain
                </Button>
                <Button variant="outline" size="sm" onClick={() => runAI('refactor')} disabled={isRunning} className="justify-start">
                  <Wrench className="w-4 h-4" /> Refactor
                </Button>
                <Button variant="outline" size="sm" onClick={() => runAI('fix')} disabled={isRunning} className="justify-start">
                  <FileCog className="w-4 h-4" /> Fix
                </Button>
                <Button variant="outline" size="sm" onClick={() => runAI('tests')} disabled={isRunning} className="justify-start">
                  <FileCheck className="w-4 h-4" /> Tests
                </Button>
                <Button variant="outline" size="sm" onClick={() => runAI('docs')} disabled={isRunning} className="justify-start">
                  <FileCode className="w-4 h-4" /> Docs
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{panelHelp}</div>
            </div>
          </TabsContent>

          {/* Chat tab */}
          <TabsContent value="chat" className="flex-1 m-0 pt-2">
            <div className="space-y-2">
              <Input value={activeFileName || ''} readOnly className="h-8" placeholder="File name" />
              <Textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Ask for changes, features, or code (optional). If left empty, actions run on code directly."
                className="min-h-[80px]"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => runAI('chat')} disabled={isRunning}>
                  <Sparkles className="w-4 h-4 mr-1" /> {isRunning ? 'Running...' : 'Run'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPrompt('')} disabled={isRunning}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Output */}
        <div className="flex-1 min-h-0">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground">
              {lastAction === 'none' ? 'No output yet' : `Result: ${lastAction}`}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={copyOutput} disabled={!output}>
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
              <Button size="sm" onClick={applyOutput} disabled={!output}>
                <Check className="w-4 h-4 mr-1" /> Apply
              </Button>
            </div>
          </div>
          <ScrollArea className="h-[180px] border rounded-md p-3 bg-muted/30">
            <pre className="text-xs whitespace-pre-wrap break-words">{output || '• Output will appear here.'}</pre>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}